import { resolveApiBaseUrl } from "./apiBase";

const API_URL = resolveApiBaseUrl();

const getAuthToken = (): string | null => {
  const token = localStorage.getItem("idToken") || localStorage.getItem("authToken");
  return token ? token.trim() : null;
};

export async function downloadMongoExport(collectionNames: string[] | "all"): Promise<void> {
  const token = getAuthToken();
  if (!token) {
    throw new Error("Not authenticated");
  }
  const q =
    collectionNames === "all"
      ? "collections=all"
      : `collections=${encodeURIComponent(collectionNames.join(","))}`;
  const res = await fetch(`${API_URL}/auth/admin/mongo/export-data?${q}`, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(data.message || `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition");
  let filename = "labwick-mongo-export.json";
  if (cd) {
    const m = /filename="([^"]+)"/.exec(cd) || /filename=([^;]+)/.exec(cd);
    if (m?.[1]) {
      filename = m[1].trim().replace(/["']/g, "");
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export type ImportDryRunPreviewRow =
  | {
      collection: string;
      mode: "replace";
      wouldDeleteExisting: number;
      wouldInsert: number;
    }
  | {
      collection: string;
      mode: "merge";
      wouldInsert: number;
      wouldUpdate: number;
      wouldUnchanged: number;
      wouldInsertNoMatchKey: number;
    };

export type ImportDryRunResponse = {
  success: boolean;
  mode?: "replace" | "merge";
  fileDatabase?: string | null;
  fileExportedAt?: string | null;
  preview?: ImportDryRunPreviewRow[];
  collectionsInFile?: string[];
  message?: string;
};

export async function mongoImportDryRun(
  file: File,
  mode: "replace" | "merge",
  collections: string[] | "all"
): Promise<ImportDryRunResponse> {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("mode", mode);
  fd.append("collections", collections === "all" ? "all" : JSON.stringify(collections));
  const token = getAuthToken();
  const res = await fetch(`${API_URL}/auth/admin/mongo/import-data/dry-run`, {
    method: "POST",
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  const data = (await res.json().catch(() => ({}))) as ImportDryRunResponse;
  if (!res.ok) {
    throw new Error(data.message || `HTTP ${res.status}`);
  }
  return data;
}

export type ImportApplyResponse = {
  success: boolean;
  mode?: string;
  results?: { collection: string; mode: string; applied: boolean }[];
  message?: string;
};

export async function mongoImportApply(
  file: File,
  mode: "replace" | "merge",
  collections: string[] | "all"
): Promise<ImportApplyResponse> {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("mode", mode);
  fd.append("collections", collections === "all" ? "all" : JSON.stringify(collections));
  fd.append("confirm", "true");
  const token = getAuthToken();
  const res = await fetch(`${API_URL}/auth/admin/mongo/import-data/apply`, {
    method: "POST",
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  const data = (await res.json().catch(() => ({}))) as ImportApplyResponse;
  if (!res.ok) {
    throw new Error(data.message || `HTTP ${res.status}`);
  }
  return data;
}
